// Central registry of every keyboard shortcut in the map viewer. This is the
// single source of truth: the in-app shortcuts reference renders from it, and
// (future) the scattered key handlers can match against it via ./match so the
// documented binding and the behaviour can never drift.
//
// Adding/renaming a shortcut? Edit it here and nowhere else for the reference to
// stay correct. Behaviour still lives in the individual hooks for now (the wiring
// of those hooks to this registry is a deferred, separate pass).
const arrows = [
    { key: 'ArrowUp' }, { key: 'ArrowDown' }, { key: 'ArrowLeft' }, { key: 'ArrowRight' },
];
export const SHORTCUTS = [
    // ----- Navigation -----
    { id: 'nav.pan', category: 'navigation', label: 'Pan the map', chords: arrows },
    { id: 'nav.rotate', category: 'navigation', label: 'Rotate', chords: [{ key: 'ArrowLeft', shift: true }, { key: 'ArrowRight', shift: true }] },
    { id: 'nav.tilt', category: 'navigation', label: 'Tilt', chords: [{ key: 'ArrowUp', shift: true }, { key: 'ArrowDown', shift: true }] },
    { id: 'nav.zoom', category: 'navigation', label: 'Zoom in / out', chords: [{ key: '+' }, { key: '-' }] },
    { id: 'nav.cancelFlight', category: 'navigation', label: 'Stop camera animation', chords: [{ key: ' ' }] },
    { id: 'nav.north', category: 'navigation', label: 'Face north', chords: [{ key: 'n' }] },
    { id: 'nav.nadir', category: 'navigation', label: 'Look straight down', chords: [{ key: 'u' }] },
    { id: 'nav.angled', category: 'navigation', label: 'Reset to angled view', chords: [{ key: 'r' }] },
    { id: 'nav.flyToProject', category: 'navigation', label: 'Fly to project area', chords: [{ key: 'Home' }] },
    // ----- Tools (Cmd/Ctrl + Shift + key) -----
    { id: 'tool.placemark', category: 'tools', label: 'Placemark tool', chords: [{ key: 'n', ctrlOrCmd: true, shift: true }] },
    { id: 'tool.polygon', category: 'tools', label: 'Polygon tool', chords: [{ key: 'p', ctrlOrCmd: true, shift: true }] },
    { id: 'tool.path', category: 'tools', label: 'Path tool', chords: [{ key: 't', ctrlOrCmd: true, shift: true }] },
    { id: 'tool.measure', category: 'tools', label: 'Measure tool', chords: [{ key: 'm', ctrlOrCmd: true, shift: true }] },
    { id: 'tool.label', category: 'tools', label: 'Label tool', chords: [{ key: 'l', ctrlOrCmd: true, shift: true }] },
    // ----- Drawing (while a polygon or path is mid-draw) -----
    // Only the polygon tool finishes on Enter; paths finish on double-click and
    // measurements auto-finish on the second click — hence the note + scoping.
    { id: 'draw.finish', category: 'drawing', label: 'Finish polygon', chords: [{ key: 'Enter' }], note: 'Paths finish by double-click' },
    { id: 'draw.removePoint', category: 'drawing', label: 'Remove last point', chords: [{ key: 'Backspace' }, { key: 'Delete' }], note: 'Polygon & path' },
    { id: 'draw.cancel', category: 'drawing', label: 'Cancel drawing', chords: [{ key: 'Escape' }], note: 'Polygon & path' },
    // ----- Editing (with a feature selected) -----
    // Notes scope each shortcut to the tools that actually implement it (audited
    // against the per-tool keydown handlers). Placemark and label have no undo.
    { id: 'edit.undo', category: 'editing', label: 'Undo', chords: [{ key: 'z', ctrlOrCmd: true }], note: 'Polygon, path, measure & sketch' },
    { id: 'edit.redo', category: 'editing', label: 'Redo', chords: [{ key: 'z', ctrlOrCmd: true, shift: true }], note: 'Polygon, path, measure & sketch' },
    { id: 'edit.duplicate', category: 'editing', label: 'Duplicate polygon', chords: [{ key: 'd', ctrlOrCmd: true }] },
    { id: 'edit.deleteVertex', category: 'editing', label: 'Delete selected vertex', chords: [{ key: 'Backspace' }, { key: 'Delete' }], note: 'Polygon & path' },
    { id: 'edit.deleteLabel', category: 'editing', label: 'Delete selected label', chords: [{ key: 'Delete' }] },
    // ----- General -----
    { id: 'general.deactivate', category: 'general', label: 'Close tool / deselect', chords: [{ key: 'Escape' }] },
    { id: 'general.help', category: 'general', label: 'Show keyboard shortcuts', chords: [{ key: '?' }] },
];
export const SHORTCUTS_BY_ID = SHORTCUTS.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
// Display order + human labels for the reference's category groupings.
export const SHORTCUT_CATEGORY_ORDER = [
    'navigation', 'tools', 'drawing', 'editing', 'general',
];
export const SHORTCUT_CATEGORY_LABELS = {
    navigation: 'Map navigation',
    tools: 'Markup tools',
    drawing: 'While drawing',
    editing: 'Editing a feature',
    general: 'General',
};
